import { useCallback } from "react";
import { atom, useRecoilValue, useRecoilRefresher_UNSTABLE, atomFamily, selectorFamily } from "recoil";
import axios from "axios";
import { userState } from "./UserState";
import { isEqualObject } from "../utils/FPEUtils";
import { COMMUNICATE_ERROR_MESSAGE_COPILOT_LIST, COMMUNICATE_ERROR_MESSAGE_COPI_SETTING_LIST, COMMUNICATE_ERROR_MESSAGE_GET_LINKABLE_FLIGHT_PLAN, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_UNLINKED_COPILOT } from "../constants/ErrorMessage";

// API
// FOSTER-CoPilot設定 CoPilot一覧画面で使用するCoPilot一覧
const _listCoPilot = async (customer) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'get',
		url: `${apiUrl}/links/copilot_list`,
		params: {
			'customer_code_operator': customer
		},
		headers: { 
			'x-api-key': apiKey
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_COPILOT_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	const data = axios(config)
		.then(response => {
			// console.log(JSON.stringify(response.data));
			const list = response.data || [];
			return list.sort((a, b) => a.sbd_imei.localeCompare(b.sbd_imei));
		})
		.catch(e => {
			console.log('Failed to retrieve copilot list.', e);
			return [];
		});
	return data;
}

// CoPilot設定更新
const _updateCoPilotSetting = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/links/copilot_update`,
		headers: { 
			'x-api-key': apiKey
		},
		data: data,
	};

	await axios(config)
		.catch(e => {
			console.log('Failed to update copilot setting.', e);
			throw e;
		});
};

// FOSTER-CoPilot設定 CoPilot紐づけ画面で使用する紐づけ情報一覧
const _listCoPilotLinkage = async (group) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'get',
		url: `${apiUrl}/links/copi_setting_list`,
		params: {
			'usergroup_id': group
		},
		headers: { 
			'x-api-key': apiKey
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_COPI_SETTING_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	const data = axios(config)
		.then(response => {
			// console.log(JSON.stringify(response.data));
			const list = response.data || [];
			return list.sort((a, b) => a.aircraft_code.localeCompare(b.aircraft_code));
		})
		.catch(e => {
			console.log('Failed to retrieve copilot linkage list.', e);
			return [];
		});
	return data;
}

// 機体と紐付いていないCoPilot一覧
const _listUnlinkedCoPilot = async (customer) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'get',
		url: `${apiUrl}/links/unlinked_copilot`,
		params: {
			'customer_code': customer
		},
		headers: { 
			'x-api-key': apiKey
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_UNLINKED_COPILOT,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	const data = axios(config)
		.then(response => {
			// console.log(JSON.stringify(response.data));
			const list = response.data || [];
			return list;
		})
		.catch(e => {
			console.log('Failed to retrieve unlinked_copilot list.', e);
			return [];
		});
	return data;
}

// 計画経路取得
const _getLinkableFlightPlan = async (aircraftCode) => {
	const apiUrl = process.env.REACT_APP_FLIGHTPLAN_API_URL;
	const apiKey = process.env.REACT_APP_FLIGHTPLAN_API_KEY;
	const config = {
		method: 'get',
		url: `${apiUrl}/flightplan/get_linkable_flight_plan`,
		params: {
			'aircraft_code': aircraftCode
		},
		headers: { 
			'x-api-key': apiKey
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_GET_LINKABLE_FLIGHT_PLAN,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};

	const data = axios(config)
		.then(response => {
			const list = response.data || [];
			return list.sort((a, b) => a.etd_utc.localeCompare(b.etd_utc));
		})
		.catch(e => {
			console.log('Failed to retrieve TODO list.', e);
			return [];
		});
	return data;
}

// 機体・プラン手動紐付け、紐付け解除
const _linkFlightPlan = async (data, link = true) => {
	const apiUrl = process.env.REACT_APP_FLIGHTPLAN_API_URL;
	const apiKey = process.env.REACT_APP_FLIGHTPLAN_API_KEY;
	const path = link ? "link_flight_plan" : "unlink_flight_plan";
	const config = {
		method: 'post',
		url: `${apiUrl}/flightplan/${path}`,
		headers: { 
			'x-api-key': apiKey
		},
		data,
	};

	return await axios(config)
		.catch(e => {
			console.log(`Failed to ${path} update.`, e);
			throw e;
		});
};

// CoPilot設定変更（FOSTER-CoPilot設定 CoPilot紐づけ画面で更新操作を行った時に呼び出す）
const _updateCoPilotLinkage = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/links/copi_update_setting`,
		headers: {
			'x-api-key': apiKey
		},
		data: data
	};

	return await axios(config)
		.catch(e => {
			console.log('Failed to copilot linkage update.', e);
			throw e;
		});
};

/* FOSTER-CoPilot設定 CoPilot一覧画面用CoPilot一覧state */
const coPilotListState = atomFamily({
	key: 'coPilotListState',
	default: selectorFamily({
		key: 'coPilotListStateSelector',
		get: (customer) => ()=> {
			return _listCoPilot(customer);
		}
	})
});

/* FOSTER-CoPilot設定 CoPilot紐づけ画面用機体一覧state */
const coPilotLinkageListState = atomFamily({
	key: 'coPilotLinkageListState',
	default: selectorFamily({
		key: 'coPilotLinkageListStateSelector',
		get: (group) => () => {
			return _listCoPilotLinkage(group);
		}
	})
});

/* 機体に紐づいていないCoPilot一覧state */
export const freeCoPilotListState = atomFamily({
	key: 'freeCoPilotListState',
	default: selectorFamily({
		key: 'freeCoPilotListStateSelector',
		get: (customer) => () => {
			return _listUnlinkedCoPilot(customer);
		},
	})
});

/* CoPi紐づけに使用する計画経路一覧のState */
export const linkableFlightPlanState = atomFamily({
	key: 'linkableFlightPlanState',
	default: selectorFamily({
		key: 'linkableFlightPlanSelector',
		get: (aircraftCode) => () => {
			return _getLinkableFlightPlan(aircraftCode);
		}
	})
});

export const coPilotListFilterWordState = atom({
	key: 'coPilotListFilterWordState',
	default: ''
});

export const useCoPilotList = () => {
	const user = useRecoilValue(userState);
	const coPilotList = useRecoilValue(coPilotListState(user.customer));

	const refresher = useRecoilRefresher_UNSTABLE(coPilotListState(user.customer));

	const updateCoPilotSetting = useCallback(data => {
		const require = [
			'interval',
			'remarks'
		];
		if (!require.every(v => v in data)) {
			throw new Error('Unset require parameter');
		}

		const supportProperties = [
			"sbd_imei",
			"copilot_serial_no",
			"copilot_product_no",
			"customer_code_contractor",
			"customer_code_operator",
			"customer_code_payer",
			"interval",
			"remarks",
			"update_time",
			"update_by_user",
			"api_exec_user"
		];
		const updateData = {};
		supportProperties.forEach(propName => {
			updateData[propName] = data[propName];
		});

		return _updateCoPilotSetting(updateData)
			.then(refresher);
	}, [refresher]);

	return {
		coPilotList,
		updateCoPilotSetting,
		refreshCoPilotList: refresher,
	}
};

export const useCoPilotLinkageList = () => {
	const user = useRecoilValue(userState);
	const coPilotSettingList = useRecoilValue(coPilotLinkageListState(user.group));

	const coPilotLinkageListRefresher = useRecoilRefresher_UNSTABLE(coPilotLinkageListState(user.group));
	const freeCoPilotListRefresher = useRecoilRefresher_UNSTABLE(freeCoPilotListState(user.customer));
	const coPilotListRefresher = useRecoilRefresher_UNSTABLE(coPilotListState(user.customer));
	const freeCoPilotList = useRecoilValue(freeCoPilotListState(user.customer));

	const refresher = useCallback(() => {
		coPilotLinkageListRefresher();
		freeCoPilotListRefresher();
	}, [coPilotLinkageListRefresher, freeCoPilotListRefresher]);

	const updateCoPilotLinkage = useCallback(data => {
		const require = [
			"customer_code",
			"aircraft_code",
			"aircraft_icon_no",
			"sbd_imei",
			"approach_alert_setting_flag",
			"approach_alert_h_threshold_nm",
			"approach_alert_v_threshold_ft",
			"route_deviation_alert_setting_flag",
			"route_deviation_alert_threshold_nm",
			'flightplan_id',
			'api_exec_user',
		];
		if(!require.every(v => v in data)) {
			throw new Error('Unset require parameter');
		};

		const before = coPilotSettingList.find(v => v.aircraft_code === data.aircraft_code);

		const updateCopilotSupportProperties = [
			"customer_code",
			"aircraft_code",
			"aircraft_icon_no",
			"sbd_imei",
			"interval",
			"unupdated_alert_setting_flag",
			"unupdated_alert_threshold_min",
			"approach_alert_setting_flag",
			"approach_alert_h_threshold_nm",
			"approach_alert_v_threshold_ft",
			"route_deviation_alert_setting_flag",
			"route_deviation_alert_threshold_nm",
			"aircraft_update_by_user",
			"aircraft_update_time",
			"aircraft_copilot_update_by_user",
			"aircraft_copilot_update_time",
			"copilot_update_by_user",
			"copilot_update_time",
			"unupdated_alert_update_time",
			"approach_alert_update_time",
			"route_deviation_alert_update_time",
		];
		const updateCopilotData = {};
		const beforeCopilotData = {};
		updateCopilotSupportProperties.forEach(propName => {
			updateCopilotData[propName] = data[propName];
			beforeCopilotData[propName] = before[propName];
		});

		const changeSbdImei = before && before.sbd_imei !== updateCopilotData.sbd_imei;
		if (changeSbdImei) {
			const coPi = freeCoPilotList.find(p => p.sbd_imei === updateCopilotData.sbd_imei);
			if (coPi) {
				updateCopilotData.copilot_update_time = coPi.copilot_update_time;
				updateCopilotData.copilot_update_by_user = coPi.copilot_update_by_user;
			}
		}

		const updatePlannedSupportProperties = [
			"aircraft_code",
			"flightplan_id"
		]
		const updatePlannedData = {};
		const beforePlannedData = {};
		updatePlannedSupportProperties.forEach(propName => {
			updatePlannedData[propName] = data[propName];
			beforePlannedData[propName] = before[propName];
		});

		
		const promises = [];
		let execUpdateCoPilot = false; 
		if (!isEqualObject(updateCopilotData, beforeCopilotData)) {
			updateCopilotData.api_exec_user = data.api_exec_user;
			promises.push(_updateCoPilotLinkage(updateCopilotData));
			execUpdateCoPilot = true;
		}
		if (!isEqualObject(updatePlannedData, beforePlannedData)) {
			const link = !!updatePlannedData.flightplan_id;
			if (link) {
				updatePlannedData.target_flightplan_id = updatePlannedData.flightplan_id;
				delete updatePlannedData.flightplan_id;
				updatePlannedData.current_flightplan_id = beforePlannedData.flightplan_id;
			} else {
				updatePlannedData.flightplan_id = beforePlannedData.flightplan_id;
			}
			promises.push(_linkFlightPlan(updatePlannedData, link));
		}

		return new Promise((resolve, reject) => {
			if(promises.length === 0) {
				resolve();
			}
			Promise.allSettled(promises).then((results) => {
				const success = results.filter(result => result.status === 'rejected').length === 0;
				if (success) {
					coPilotLinkageListRefresher();
					if (changeSbdImei) {
						freeCoPilotListRefresher();
						coPilotListRefresher();
					}
					resolve();
				} else {
					let coPilotResult;
					let flightPlanResult;
					if (results.length === 2) {
						coPilotResult = results[0];
						flightPlanResult = results[1];
					} else if (execUpdateCoPilot) {
						coPilotResult = results[0];
					} else {
						flightPlanResult = results[0];
					}
	
					const ret = {};
					if (coPilotResult) {
						ret.coPilot = {
							progress: coPilotResult.status === 'fulfilled',
							reason: coPilotResult.status === 'rejected' ? coPilotResult.reason : null
						};
					}
					if (flightPlanResult) {
						ret.flightPlan = {
							progress: flightPlanResult.status === 'fulfilled',
							reason: flightPlanResult.status === 'rejected' ? flightPlanResult.reason : null
						};
					}
					reject(ret);
				}
			});
		});

	}, [coPilotSettingList, coPilotLinkageListRefresher, coPilotListRefresher, freeCoPilotListRefresher, freeCoPilotList]);

	return {
		coPilotSettingList,
		updateCoPilotLinkage,
		refreshCoPilotLinkageList: refresher,
	}
};

